import React, { useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { PlusIcon, type PlusIconHandle } from "@/components/ui/plus-icon";
import { cn } from "@/lib/utils";

export type SimulatorPolicyAddButtonKind = "group" | "rule";

const LABEL: Record<SimulatorPolicyAddButtonKind, string> = {
  group: "Add group",
  rule: "Add rule",
};

export type SimulatorPolicyAddButtonProps = {
  kind: SimulatorPolicyAddButtonKind;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
};

export function SimulatorPolicyAddButton({
  kind,
  onClick,
  className,
  disabled,
}: SimulatorPolicyAddButtonProps): React.ReactElement {
  const iconRef = useRef<PlusIconHandle | null>(null);

  const startIconAnimation = useCallback(() => {
    void iconRef.current?.startAnimation();
  }, []);

  const stopIconAnimation = useCallback(() => {
    void iconRef.current?.stopAnimation();
  }, []);

  const iconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startIconAnimation,
      onMouseEnter: startIconAnimation,
      onMouseLeave: stopIconAnimation,
    }),
    [startIconAnimation, stopIconAnimation]
  );

  return (
    <Button
      type="button"
      variant="gradient"
      size="sm"
      data-font="primary"
      data-policy-add-button={kind}
      disabled={disabled}
      className={cn(
        "h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal",
        className
      )}
      onClick={onClick}
      {...iconHoverHandlers}
    >
      <PlusIcon
        ref={iconRef}
        data-icon="inline-start"
        size={16}
        className="shrink-0 text-current"
        aria-hidden
      />
      <span>{LABEL[kind]}</span>
    </Button>
  );
}
